<script setup>
import { ref, computed, watch } from 'vue'
import FormButton from '@/components/Atoms/FormButton.vue'
import Alert from '@/components/Atoms/Alert.vue'
import SiteAdminService, { isPaywallSiteUnconfigured } from '@/services/SiteAdminService'
import { useSiteGiving } from '@/composables/useSiteGiving'
import { formatMoney } from '@/Utils/siteTransactions'

// Overview card for the broadcast template's giving (contract §7): with
// native donations, the last 30 days + twelve monthly bars from castr
// `GET /sites/{id}/giving/summary`, net of dashboard refunds. Renders nothing
// while giving is off.
const props = defineProps({
	siteId: {
		type: String,
		required: true,
	},
})
const giving = useSiteGiving(() => props.siteId)
giving.load()

const mode = computed(() => giving.mode.value)
const config = computed(() => giving.giving.value)

const summary = ref(null)
const summaryLoading = ref(false)
const summaryError = ref(null)
const loadSummary = async () => {
	summaryLoading.value = true
	try {
		summary.value = await SiteAdminService.getGivingSummary(props.siteId, { days: 30 })
		summaryError.value = null
	} catch (err) {
		summary.value = null
		summaryError.value = err
	}
	summaryLoading.value = false
}
watch(mode, (m) => { if (m === 'native') loadSummary() }, { immediate: true })

const currency = computed(() => summary.value?.currency || config.value?.native?.currency || 'USD')
const last30 = computed(() => summary.value?.last30 || { count: 0, total: 0 })
const months = computed(() => {
	const list = Array.isArray(summary.value?.byMonth) ? summary.value.byMonth : []
	const max = Math.max(0, ...list.map((m) => Number(m.total) || 0))
	return list.slice(-12).map((m) => ({
		...m,
		total: Number(m.total) || 0,
		count: Number(m.count) || 0,
		pct: max > 0 ? Math.max(4, Math.round(((Number(m.total) || 0) / max) * 100)) : 0,
		label: monthLabel(m.month),
	}))
})
function monthLabel(ym) {
	const [y, mo] = String(ym || '').split('-').map(Number)
	if (!y || !mo) return ym || ''
	return new Date(Date.UTC(y, mo - 1, 1)).toLocaleDateString(undefined, { month: 'short', timeZone: 'UTC' })
}
const unlinked = computed(() => isPaywallSiteUnconfigured(summaryError.value))
const refunded = computed(() => Number(summary.value?.refunded) || 0)
const refundedNote = computed(() => {
	const gross = Number(summary.value?.gross) || 0
	return `${formatMoney(refunded.value, currency.value)} refunded all time${gross > 0 ? ` of ${formatMoney(gross, currency.value)} given` : ''}.`
})
const lastGift = computed(() => (summary.value?.lastDonationAt ? new Date(summary.value.lastDonationAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : null))
</script>

<template>
	<!-- Native donations -->
	<section v-if="mode === 'native'" class="rounded-lg bg-surface-2 p-4 sm:p-5" data-testid="giving-summary-card" data-mode="native" :aria-busy="summaryLoading">
		<div class="mb-2 flex flex-wrap items-start justify-between gap-2">
			<div class="min-w-0">
				<h2 class="mb-0.5 text-headline font-semibold text-surface-text-2">Giving · last 30 days</h2>
				<p class="mb-0 text-tiny text-surface-7">Successful gifts on your site, net of refunds.</p>
			</div>
			<FormButton type="secondary" size="sm" class="shrink-0" :to="{ name: 'SiteDonations', params: { siteId } }" data-testid="giving-summary-link">View donations</FormButton>
		</div>
		<p v-if="unlinked" class="mb-0 rounded-lg bg-surface-3 px-3 py-2.5 text-tiny text-surface-7" data-testid="giving-summary-unlinked">Donations appear once the site is linked to your Paywall account — publish the site first.</p>
		<Alert v-else-if="summaryError && !summaryLoading" type="error" class="!mb-0">{{ summaryError.message || 'Could not load the giving summary.' }}</Alert>
		<p v-else-if="summary && summary.enabled === false" class="mb-0 rounded-lg bg-surface-3 px-3 py-2.5 text-tiny text-surface-7" data-testid="giving-summary-disabled">
			{{ summary.reason === 'no_product' ? 'Save the Giving card under Settings › Broadcast to finish setting up donations.' : summary.reason === 'not_supported' ? 'Donations need a newer payment service version.' : 'Donations are temporarily unavailable on the payment service. Gifts will show here once they are back on.' }}
		</p>
		<template v-else>
			<dl class="grid grid-cols-2 gap-3 sm:grid-cols-3" data-testid="giving-summary-totals">
				<div class="min-w-0 rounded-lg bg-surface-3 px-3 py-2">
					<dt class="mb-0.5 truncate text-tiny text-surface-7">Gifts · 30 days</dt>
					<dd class="mb-0 truncate text-sm font-semibold text-white" data-testid="giving-summary-count">{{ summaryLoading && !summary ? '—' : last30.count }}</dd>
				</div>
				<div class="min-w-0 rounded-lg bg-surface-3 px-3 py-2">
					<dt class="mb-0.5 truncate text-tiny text-surface-7">Total · 30 days</dt>
					<dd class="mb-0 truncate text-sm font-semibold text-white" data-testid="giving-summary-total">{{ summaryLoading && !summary ? '—' : formatMoney(last30.total, currency) }}</dd>
				</div>
				<div class="min-w-0 rounded-lg bg-surface-3 px-3 py-2">
					<dt class="mb-0.5 truncate text-tiny text-surface-7">All time</dt>
					<dd class="mb-0 truncate text-sm font-semibold text-white" data-testid="giving-summary-lifetime">{{ summary ? `${formatMoney(summary.total, currency)} · ${summary.count} ${summary.count === 1 ? 'gift' : 'gifts'}` : '—' }}</dd>
				</div>
			</dl>
			<div v-if="months.length" class="mt-3" data-testid="giving-summary-months">
				<div class="flex h-16 items-end gap-1" role="img" :aria-label="`Monthly giving: ${months.map((m) => `${m.label} ${formatMoney(m.total, currency)}`).join(', ')}`">
					<div v-for="m in months" :key="m.month" class="group relative flex h-full flex-1 flex-col justify-end" :title="`${m.label}: ${formatMoney(m.total, currency)} · ${m.count} ${m.count === 1 ? 'gift' : 'gifts'}`">
						<div class="w-full rounded-t-sm transition-colors" :class="m.total > 0 ? 'bg-main group-hover:bg-main-light' : 'bg-surface-4'" :style="{ height: `${m.total > 0 ? m.pct : 4}%` }"></div>
					</div>
				</div>
				<div class="mt-1 flex gap-1 text-[10px] text-surface-6">
					<span v-for="m in months" :key="`l-${m.month}`" class="flex-1 truncate text-center">{{ m.label }}</span>
				</div>
			</div>
			<p v-if="summary && !summary.count" class="mb-0 mt-3 text-tiny text-surface-7" data-testid="giving-summary-empty">No gifts yet. They show up here as soon as someone gives on your site.</p>
			<p v-else-if="lastGift || refunded > 0" class="mb-0 mt-3 text-tiny text-surface-7">
				<span v-if="lastGift">Last gift {{ lastGift }}.</span> <span v-if="refunded > 0" data-testid="giving-summary-refunded">{{ refundedNote }}</span>
			</p>
		</template>
	</section>

</template>
